import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { validatePhoneNumber, formatPhoneNumberInput, maskFirstName, maskPhoneNumber } from '../../utils/phoneValidation';
import './EmergencyContactPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface EmergencyContactData {
  firstName: string;
  lastName: string;
  relationship: string;
  phoneNumber: string;
}

const EmergencyContactPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passcode = location.state?.passcode || localStorage.getItem('currentPasscode');

  const [currentData, setCurrentData] = useState<EmergencyContactData>({
    firstName: '',
    lastName: '',
    relationship: '',
    phoneNumber: ''
  });
  const [formData, setFormData] = useState<EmergencyContactData>({
    firstName: '',
    lastName: '',
    relationship: '',
    phoneNumber: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!passcode) {
      navigate('/');
      return;
    }
    fetchCurrentData();
  }, [passcode, navigate]);

  const fetchCurrentData = async () => {
    setIsLoadingData(true);
    try {
      const response = await fetch(`${API_BASE_URL}/user-emergency-contact/${passcode}`);
      const result = await response.json();
      
      if (response.ok && result.success) {
        // Extract data from the nested structure
        const emergencyData = {
          firstName: result.data.first_name || '',
          lastName: result.data.last_name || '',
          relationship: result.data.relationship || '',
          phoneNumber: result.data.phone_number || ''
        };
        setCurrentData(emergencyData);
        // Don't pre-fill form data - keep form empty for new input
        setFormData({
          firstName: '',
          lastName: '',
          relationship: '',
          phoneNumber: ''
        });
      } else {
        setMessage(result.message || 'Failed to load emergency contact data');
      }
    } catch (error) {
      setMessage('Failed to connect to server');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    
    // Handle phone number formatting (but not validation)
    if (name === 'phoneNumber') {
      processedValue = formatPhoneNumberInput(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setMessage('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setMessage('Last name is required');
      return false;
    }
    if (!formData.relationship.trim()) {
      setMessage('Relationship is required');
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      setMessage('Phone number is required');
      return false;
    }
    
    // Validate phone number format
    const phoneValidation = validatePhoneNumber(formData.phoneNumber);
    if (!phoneValidation.isValid) {
      setMessage('Phone number: ' + (phoneValidation.error || 'Invalid format'));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      // Convert to snake_case format expected by backend
      const requestData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone_number: validatePhoneNumber(formData.phoneNumber).formatted,
        relationship: formData.relationship
      };

      const response = await fetch(`${API_BASE_URL}/update-emergency-contact/${passcode}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setCurrentData(formData);
        setMessage('Emergency contact updated successfully!');
      } else {
        setMessage(result.message || 'Failed to update emergency contact');
      }
    } catch (error) {
      setMessage('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSuccess = () => {
    navigate('/success', { state: { passcode } });
  };

  if (isLoadingData) {
    return (
      <div className="emergency-contact-container">
        <div className="emergency-contact-card">
          <div className="loading-spinner-large"></div>
          <p>Loading emergency contact information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="emergency-contact-container">
      <div className="emergency-contact-card">
        <div className="emergency-contact-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="emergency-contact-title">Update Emergency Contact</h1>
          <p className="emergency-contact-subtitle">
            Update your emergency contact information
          </p>
        </div>

        <div className="current-info-section">
          <h2 className="current-info-title">Current Emergency Contact</h2>
          <div className="current-info-grid">
            <div className="info-item">
              <span className="info-label">Name:</span>
              <span className="info-value">{maskFirstName(currentData.firstName)} {currentData.lastName}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Relationship:</span>
              <span className="info-value">{currentData.relationship}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Phone:</span>
              <span className="info-value">{maskPhoneNumber(currentData.phoneNumber)}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="update-form">
          <div className="form-divider">
            <div className="divider-line"></div>
            <span className="divider-text">Update Information</span>
            <div className="divider-line"></div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="Enter first name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="relationship">Relationship *</label>
              <select
                id="relationship"
                name="relationship"
                value={formData.relationship}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                <option value="">Select Relationship</option>
                <option value="Parent">Parent</option>
                <option value="Guardian">Guardian</option>
                <option value="Spouse">Spouse</option>
                <option value="Sibling">Sibling</option>
                <option value="Child">Child</option>
                <option value="Friend">Friend</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number *</label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="(555)555-5555"
              />
            </div>
          </div>

          <button
            type="submit"
            className={`submit-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="loading-spinner"></div>
            ) : (
              'Update Emergency Contact'
            )}
          </button>

          {message && (
            <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}
        </form>

        <div className="emergency-contact-footer">
          <button onClick={handleBackToSuccess} className="back-button">
            ← Back to Options
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmergencyContactPage;
